import React, { Component } from 'react';

import {
  addDays,
  subDays,
  startOfWeek,
  startOfMonth,
  getDaysInMonth,
  startOfQuarter,
  addMonths,
  differenceInCalendarDays,
  subMonths,
  subQuarters,
  addQuarters,
  format,
} from 'date-fns';
import { Grid, Snackbar } from '@material-ui/core';

import DashboardDatePicker from '../components/Dashboard/DashboardDatePicker';
import DashboardMenu from '../components/Dashboard/DashboardMenu';
import BookingInfo from '../components/Dashboard/BookingInfo';
import StudioProgressInfo from '../components/Dashboard/StudioProgressInfo';
import AccessoriesProgressInfo from '../components/Dashboard/AccessoriesProgressInfo';

import {
  dashboardKeys,
  DATE_PICKER_OPTION,
  PROGRESS_INFO,
} from '../constants/general';
import { formatDateOnlyStringFromLocalTime } from '../helpers/helpers';
import {
  fetchBookingCount,
  fetchBookingEmployeeTypeCount,
  fetchBookingBookingStatusCount,
  fetchActualVsPlannedAllCounts,
  fetchActualVsPlannedCountPerLocation,
  fetchActualVsPlannedRange,
  fetchPlannedCostByLocationType,
  fetchAllPlannedCost,
} from '../helpers/externalRequestsDashboardView';

import i18n from '../i18n';
import '../assets/stylesheets/DashboardView.css';

const initialState = {
  bookingLocationCount: [],
  bookingEmployeeTypeCount: [],
  bookingStatusCount: [],
  studioRtwAllMaleSum: [],
  studioMflAllMaleSum: [],
  studioRtwAllFemaleSum: [],
  studioMflAllFemaleSum: [],
  setShoeAllSum: [],
  setBagAllSum: [],
  setAccAllSum: [],
  rtwPerStudioSum: [],
  mflPerStudioSum: [],
  accPerSetSum: [],
  bagsPerSetSum: [],
  shoesPerSetSum: [],
  actualVsPlannedRangeDataStudioSum: [],
  actualVsPlannedRangeDataSetSum: [],
  selectedProgressChart: PROGRESS_INFO.BOOKING,
  selectedRangeType: DATE_PICKER_OPTION.WEEKLY,
  plannedCostsStudioSum: { fulltime: 0, freelancer: 0, contractor: 0, discrepancy: [] },
  plannedCostsSetSum: { fulltime: 0, freelancer: 0, contractor: 0, discrepancy: [] },
  plannedCostsBookingSum: { fulltime: 0, freelancer: 0, contractor: 0, discrepancy: [] },
};

class DashboardView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startOfSelectedDate: startOfWeek(new Date(), {
        weekStartsOn: 1,
      }),
      daysInRange: 7,
      snackbarMessage: '',
      snackOpen: false,
      formattedSelectedDateRange: '',
      ...initialState,
    };
  }

  componentDidMount() {
    this.getDashboardData();
  }

  getDashboardData = async () => {
    const { daysInRange, startOfSelectedDate } = this.state;
    const startDate = formatDateOnlyStringFromLocalTime(startOfSelectedDate);
    const endDate = formatDateOnlyStringFromLocalTime(
      addDays(new Date(startOfSelectedDate), daysInRange - 1)
    );

    const formattedSelectedDateRange = this.generateFormattedSelectedDateRange(
      new Date(startDate),
      new Date(endDate)
    );

    try {
      const [
        bookingLocationCount,
        bookingEmployeeTypeCount,
        bookingStatusCount,
        studioRtwAllMaleSum,
        studioMflAllMaleSum,
        studioRtwAllFemaleSum,
        studioMflAllFemaleSum,
        setShoeAllSum,
        setBagAllSum,
        setAccAllSum,
        rtwPerStudioSum,
        mflPerStudioSum,
        accPerSetSum,
        bagsPerSetSum,
        shoesPerSetSum,
        actualVsPlannedRangeDataStudioSum,
        actualVsPlannedRangeDataSetSum,
        plannedCostsStudioSum,
        plannedCostsSetSum,
        plannedCostsBookingSum
      ] = await Promise.all([
        fetchBookingCount({ startDate, endDate, }),
        fetchBookingEmployeeTypeCount({ startDate, endDate, }),
        fetchBookingBookingStatusCount({ startDate, endDate, }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'RTW',
          location: 'STUDIO',
          gender: 'male',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'MFL',
          location: 'STUDIO',
          gender: 'male',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'RTW',
          location: 'STUDIO',
          gender: 'female',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'MFL',
          location: 'STUDIO',
          gender: 'female',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'Shoes',
          location: 'SET',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'Bags',
          location: 'SET',
        }),
        fetchActualVsPlannedAllCounts({
          startDate,
          endDate,
          type: 'Accessories',
          location: 'SET',
        }),
        fetchActualVsPlannedCountPerLocation({
          startDate,
          endDate,
          type: 'RTW',
          location: 'STUDIO',
        }),
        fetchActualVsPlannedCountPerLocation({
          startDate,
          endDate,
          type: 'MFL',
          location: 'STUDIO',
        }),
        fetchActualVsPlannedCountPerLocation({
          startDate,
          endDate,
          type: 'Accessories',
          location: 'SET',
        }),
        fetchActualVsPlannedCountPerLocation({
          startDate,
          endDate,
          type: 'Bags',
          location: 'SET',
        }),
        fetchActualVsPlannedCountPerLocation({
          startDate,
          endDate,
          type: 'Shoes',
          location: 'SET',
        }),
        fetchActualVsPlannedRange({
          startDate,
          endDate,
          location: 'STUDIO',
        }),
        fetchActualVsPlannedRange({
          startDate,
          endDate,
          location: 'SET',
        }),
        fetchPlannedCostByLocationType({
          startDate,
          endDate,
          locationTypeId: 1,
        }),
        fetchPlannedCostByLocationType({
          startDate,
          endDate,
          locationTypeId: 3,
        }),
        fetchAllPlannedCost({
          startDate,
          endDate,
        }),
      ]);
      this.setState({
        startOfSelectedDate,
        formattedSelectedDateRange,
        bookingLocationCount: bookingLocationCount.data,
        bookingEmployeeTypeCount: bookingEmployeeTypeCount.data,
        bookingStatusCount: bookingStatusCount.data,
        studioRtwAllMaleSum: studioRtwAllMaleSum.data,
        studioMflAllMaleSum: studioMflAllMaleSum.data,
        studioRtwAllFemaleSum: studioRtwAllFemaleSum.data,
        studioMflAllFemaleSum: studioMflAllFemaleSum.data,
        setShoeAllSum: setShoeAllSum.data,
        setBagAllSum: setBagAllSum.data,
        setAccAllSum: setAccAllSum.data,
        rtwPerStudioSum: rtwPerStudioSum.data,
        mflPerStudioSum: mflPerStudioSum.data,
        accPerSetSum: accPerSetSum.data,
        bagsPerSetSum: bagsPerSetSum.data,
        shoesPerSetSum: shoesPerSetSum.data,
        actualVsPlannedRangeDataStudioSum:
        actualVsPlannedRangeDataStudioSum.data,
        actualVsPlannedRangeDataSetSum:
        actualVsPlannedRangeDataSetSum.data,
        plannedCostsStudioSum: plannedCostsStudioSum.data,
        plannedCostsSetSum: plannedCostsSetSum.data,
        plannedCostsBookingSum: plannedCostsBookingSum.data,
      });
    } catch {
      this.setState({
        snackbarMessage: i18n.t('dashboard:dashboardRetrieveError'),
        snackOpen: true,
        startOfSelectedDate,
        formattedSelectedDateRange,
        ...initialState,
      });
    }
  };

  onMenuItemClick = (menuItemSelected) => {
    this.setState({ selectedProgressChart: menuItemSelected });
  };

  showProgressInfo = () => {
    const {
      selectedProgressChart,
      bookingLocationCount,
      bookingEmployeeTypeCount,
      bookingStatusCount,
      studioRtwAllMaleSum,
      studioMflAllMaleSum,
      studioRtwAllFemaleSum,
      studioMflAllFemaleSum,
      setShoeAllSum,
      setBagAllSum,
      setAccAllSum,
      rtwPerStudioSum,
      mflPerStudioSum,
      accPerSetSum,
      bagsPerSetSum,
      shoesPerSetSum,
      formattedSelectedDateRange,
      actualVsPlannedRangeDataStudioSum,
      actualVsPlannedRangeDataSetSum,
      plannedCostsStudioSum,
      plannedCostsSetSum,
      plannedCostsBookingSum,
    } = this.state;

    switch (selectedProgressChart) {
      case PROGRESS_INFO.SET:
        return (
          <AccessoriesProgressInfo
            formattedSelectedDateRange={formattedSelectedDateRange}
            indexBy={dashboardKeys.location}
            setAccData={accPerSetSum}
            totalSetAccData={setAccAllSum}
            setBagData={bagsPerSetSum}
            totalSetBagData={setBagAllSum}
            setShoeData={shoesPerSetSum}
            totalSetShoeData={setShoeAllSum}
            keys={[dashboardKeys.planned, dashboardKeys.actual]}
            actualVsPlannedRangeDataSet={actualVsPlannedRangeDataSetSum}
            fullTimeCost={plannedCostsSetSum.fulltime}
            freelancerCost={plannedCostsSetSum.freelancer}
            contractorCost={plannedCostsSetSum.contractor}
            discrepancy={plannedCostsSetSum.discrepancy}
          />
        );
      case PROGRESS_INFO.STUDIO:
        return (
          <StudioProgressInfo
            formattedSelectedDateRange={formattedSelectedDateRange}
            indexBy={dashboardKeys.location}
            studioRTWData={rtwPerStudioSum}
            totalMaleStudioRTWData={studioRtwAllMaleSum}
            keys={[dashboardKeys.planned, dashboardKeys.actual]}
            studioMFLData={mflPerStudioSum}
            totalMaleStudioMFLData={studioMflAllMaleSum}
            actualVsPlannedRangeDataStudio={actualVsPlannedRangeDataStudioSum}
            fullTimeCost={plannedCostsStudioSum.fulltime}
            freelancerCost={plannedCostsStudioSum.freelancer}
            contractorCost={plannedCostsStudioSum.contractor}
            discrepancy={plannedCostsStudioSum.discrepancy}
            totalFemaleStudioRTWData={studioRtwAllFemaleSum}
            totalFemaleStudioMFLData={studioMflAllFemaleSum}
          />
        );
      default:
        return (
          <BookingInfo
            formattedSelectedDateRange={formattedSelectedDateRange}
            bookingPerLocationData={bookingLocationCount}
            bookingTypesData={bookingEmployeeTypeCount}
            bookingStatusData={bookingStatusCount}
            fullTimeCost={plannedCostsBookingSum.fulltime}
            freelancerCost={plannedCostsBookingSum.freelancer}
            contractorCost={plannedCostsBookingSum.contractor}
            discrepancy={plannedCostsBookingSum.discrepancy}
          />
        );
    }
  };

  handleDateRangeChange = (dateRangeType, date = new Date()) => {
    let daysInRange;
    let startDayOfWeek;
    let startDayOfMonth;
    let startDayOfQuarter;
    let endDate;

    switch (dateRangeType) {
      case DATE_PICKER_OPTION.MONTHLY:
        startDayOfMonth = startOfMonth(date);
        daysInRange = getDaysInMonth(startDayOfMonth);
        this.setState(
          {
            selectedRangeType: DATE_PICKER_OPTION.MONTHLY,
            startOfSelectedDate: startDayOfMonth,
            daysInRange,
          },
          () => this.getDashboardData()
        );
        break;
      case DATE_PICKER_OPTION.QUARTERLY:
        startDayOfQuarter = startOfQuarter(date);
        endDate = addMonths(startDayOfQuarter, 3);
        daysInRange = Math.abs(
          differenceInCalendarDays(startDayOfQuarter, endDate)
        );
        this.setState(
          {
            selectedRangeType: DATE_PICKER_OPTION.QUARTERLY,
            startOfSelectedDate: startDayOfQuarter,
            daysInRange,
          },
          () => this.getDashboardData()
        );
        break;
      case DATE_PICKER_OPTION.CUSTOM:
        startDayOfWeek = startOfWeek(date);
        this.setState(
          {
            selectedRangeType: DATE_PICKER_OPTION.CUSTOM,
            startOfSelectedDate: startDayOfWeek,
            daysInRange: 7,
          },
          () => this.getDashboardData()
        );
        break;
      default:
        startDayOfWeek = startOfWeek(date);
        this.setState(
          {
            selectedRangeType: DATE_PICKER_OPTION.WEEKLY,
            startOfSelectedDate: startDayOfWeek,
            daysInRange: 7,
          },
          () => this.getDashboardData()
        );
        break;
    }
  };

  generateFormattedSelectedDateRange = (startDate, endDate) => {
    const formattedStartDay = format(addDays(startDate, 1), 'MMM d, yyyy');
    const formattedEndDay = format(addDays(endDate, 1), 'MMM d, yyyy');
    return `${formattedStartDay} ${i18n.t('to')} ${formattedEndDay}`;
  };

  onDateChange = (dateSelected) => {
    const { selectedRangeType } = this.state;

    let startOfSelectedDate;
    switch (selectedRangeType) {
      case DATE_PICKER_OPTION.MONTHLY:
        startOfSelectedDate = startOfMonth(dateSelected);
        break;
      case DATE_PICKER_OPTION.QUARTERLY:
        startOfSelectedDate = startOfQuarter(dateSelected);
        break;
      default:
        startOfSelectedDate = startOfWeek(dateSelected);
        break;
    }

    this.setState({ startOfSelectedDate }, () => this.getDashboardData());
  };

  onChangeCustomStartDate = (dateSelected) => {
    const { startOfSelectedDate, daysInRange } = this.state;
    const currentEndDate = addDays(startOfSelectedDate, daysInRange);
    const newDaysInRange = Math.abs(
      differenceInCalendarDays(dateSelected, currentEndDate)
    );
    this.setState(
      { startOfSelectedDate: dateSelected, daysInRange: newDaysInRange },
      () => this.getDashboardData()
    );
  };

  onChangeCustomEndDate = (dateSelected) => {
    const { startOfSelectedDate } = this.state;
    const daysInRange = Math.abs(
      differenceInCalendarDays(dateSelected, startOfSelectedDate)
    );
    this.setState({ daysInRange }, () => this.getDashboardData());
  };

  onSelectPreviousRange = () => {
    const { startOfSelectedDate, selectedRangeType } = this.state;
    let newStartOfSelectedDate;
    let daysInRange;

    switch (selectedRangeType) {
      case DATE_PICKER_OPTION.MONTHLY:
        newStartOfSelectedDate = startOfMonth(
          subMonths(startOfSelectedDate, 1)
        );
        daysInRange = getDaysInMonth(newStartOfSelectedDate);
        break;
      case DATE_PICKER_OPTION.QUARTERLY:
        newStartOfSelectedDate = startOfQuarter(
          subQuarters(startOfSelectedDate, 1)
        );
        daysInRange = Math.abs(
          differenceInCalendarDays(startOfSelectedDate, newStartOfSelectedDate)
        );
        break;
      default:
        newStartOfSelectedDate = startOfWeek(subDays(startOfSelectedDate, 7));
        daysInRange = 7;
        break;
    }

    this.setState(
      { startOfSelectedDate: newStartOfSelectedDate, daysInRange },
      () => this.getDashboardData()
    );
  };

  onSelectNextRange = () => {
    const { startOfSelectedDate, selectedRangeType } = this.state;

    let newStartOfSelectedDate;
    let daysInRange;
    switch (selectedRangeType) {
      case DATE_PICKER_OPTION.MONTHLY:
        newStartOfSelectedDate = startOfMonth(
          addMonths(startOfSelectedDate, 1)
        );
        daysInRange = getDaysInMonth(newStartOfSelectedDate);
        break;
      case DATE_PICKER_OPTION.QUARTERLY:
        newStartOfSelectedDate = startOfQuarter(
          addQuarters(startOfSelectedDate, 1)
        );
        daysInRange = Math.abs(
          differenceInCalendarDays(startOfSelectedDate, newStartOfSelectedDate)
        );
        break;
      default:
        newStartOfSelectedDate = startOfWeek(addDays(startOfSelectedDate, 7));
        daysInRange = 7;
        break;
    }

    this.setState(
      { startOfSelectedDate: newStartOfSelectedDate, daysInRange },
      () => this.getDashboardData()
    );
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  render() {
    const {
      startOfSelectedDate,
      snackbarMessage,
      snackOpen,
      selectedRangeType,
      daysInRange,
    } = this.state;

    return (
      <>
        <Grid container className="dashboardView">
          <Grid item xs={3}>
            <DashboardDatePicker
              startOfSelectedDate={startOfSelectedDate}
              onChange={this.onDateChange}
              onSelectPreviousRange={this.onSelectPreviousRange}
              onSelectNextRange={this.onSelectNextRange}
              selectedRangeType={selectedRangeType}
              handleDateRangeChange={this.handleDateRangeChange}
              daysInRange={daysInRange}
              onChangeCustomStartDate={this.onChangeCustomStartDate}
              onChangeCustomEndDate={this.onChangeCustomEndDate}
            />
            <DashboardMenu onMenuItemClick={this.onMenuItemClick} />
          </Grid>
          <Grid item xs={9}>
            {this.showProgressInfo()}
          </Grid>
        </Grid>

        <Snackbar
          message={snackbarMessage}
          open={snackOpen}
          onClose={this.hideSnackbar}
          autoHideDuration={2000}
        />
      </>
    );
  }
}

export default DashboardView;
